import moment from 'moment';
import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { required, useTranslate } from 'react-admin';
import {
  Box, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import { getExpiryTimeGranularity, TimeGranularityLabel } from '../../../utils/timeGranularityUtils';

const calculateExpiryDate = (startDate: string | undefined, timeGranularity: ExpiryTimeGranularity | undefined) => {

  if (!startDate || !timeGranularity || !timeGranularity?.type || !timeGranularity?.value === null) {

    return undefined;

  }

  return moment(startDate).add(timeGranularity.value, timeGranularity.type).format('YYYY-MM-DD');

};

const useExpiryDate = (startDateSource: string, expiryDateSource: string, timeGranularity: ExpiryTimeGranularity | undefined) => {

  const { setValue } = useFormContext();
  const { dirtyFields } = useFormState();
  const startDate = useWatch({ name: startDateSource });
  const expiryDate = useWatch({ name: expiryDateSource });

  const calculatedExpiryDate = useMemo(() => {

    return calculateExpiryDate(startDate, timeGranularity);

  }, [startDate, timeGranularity]);

  useEffect(() => {

    if (!dirtyFields[startDateSource] || !calculatedExpiryDate) {

      return;

    }

    setValue(expiryDateSource, calculatedExpiryDate, { shouldDirty: true, shouldValidate: true });

  }, [calculatedExpiryDate, expiryDateSource, setValue, dirtyFields, startDateSource]);

  const applyExpiryDate = useCallback(() => {

    if (!calculatedExpiryDate) {

      return;

    }

    setValue(expiryDateSource, calculatedExpiryDate, { shouldDirty: true, shouldValidate: true });

  }, [calculatedExpiryDate, expiryDateSource, setValue]);

  const isOverridden = useMemo(
    () => {

      return calculatedExpiryDate != null
      && expiryDate != null
      && moment(expiryDate).diff(moment(calculatedExpiryDate), 'days') !== 0;

    },
    [expiryDate, calculatedExpiryDate],
  );

  return {
    calculatedExpiryDate,
    applyExpiryDate,
    isOverridden,
    startDate,
    expiryDate,
  };

};

const ExpiryDateHelperText = ({ text }: { text: string }) => (
  <Typography color="text.secondary"
    sx={{
      fontSize: 10,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }}>
    {text}
  </Typography>
);

export const ExpiryDateDisplay = (props: {
  startDateSource: string;
  expiryDateSource: string;
  timeGranularity?: ExpiryTimeGranularity;

  startDateLabel: string;
  expiryDateLabel: string;
  helperText: string;
  children?: React.ReactNode;
  usage: 'certifications' | 'inventoryItems';
}) => {

  const translate = useTranslate();

  const {
    calculatedExpiryDate, applyExpiryDate, isOverridden, startDate, expiryDate,
  } = useExpiryDate(props.startDateSource, props.expiryDateSource, props.timeGranularity);

  const timeGranularityRounded = useMemo(() => {

    if (!startDate || !expiryDate) {

      return props.timeGranularity ?? undefined;

    }

    return getExpiryTimeGranularity(startDate, expiryDate);

  }, [startDate, expiryDate, props.timeGranularity]);

  const minExpiryDate = useMemo(() => {

    if (!startDate) return undefined;
    return moment(startDate).startOf('day').format('YYYY-MM-DD');

  }, [startDate]);

  const isFlexibleWidth = props.usage === 'inventoryItems';
  const columnGap = isFlexibleWidth ? 2 : 1;
  const inputHeight = isFlexibleWidth ? 56 : 40;
  const dateInputSx = isFlexibleWidth ? { width: '100%', minWidth: 0 } : { width: 160 };
  const dateFieldSx = isFlexibleWidth
    ? { minWidth: 0, maxWidth: '100%', alignSelf: 'flex-start' }
    : { maxWidth: 160, alignSelf: 'flex-start' };
  let gridTemplateColumns: string;
  if (isFlexibleWidth) {

    const dateColumn = 'minmax(0, 1fr)';
    gridTemplateColumns = props.children ? `${dateColumn} max-content ${dateColumn} max-content` : `${dateColumn} max-content ${dateColumn}`;

  } else {

    gridTemplateColumns = props.children ? '160px max-content 160px max-content' : '160px max-content 160px';

  }

  return (
    <Stack spacing={props.usage === 'certifications' ? columnGap : 0} sx={{ width: isFlexibleWidth ? '100%' : undefined, minWidth: 0 }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns,
        columnGap,
        rowGap: 0,
        alignItems: 'start',
        width: isFlexibleWidth ? '100%' : undefined,
        minWidth: 0,
      }}>
        <Box sx={dateFieldSx}>
          <GrayDateInputWithUpperLabel
            source={props.startDateSource}
            label={props.startDateLabel}
            size={isFlexibleWidth ? 'medium' : 'small'}
            helperText={props.usage === 'inventoryItems' ? false : undefined}
            sx={dateInputSx}
            validate={required()}
          />
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: columnGap,
            alignSelf: 'start',
            mt: 'calc(0.5em + 16px + 0.15em)',
            height: inputHeight,
          }}
        >
          <Typography component="span" sx={{ fontWeight: 'bold', lineHeight: 1 }}>+</Typography>
          <Tooltip title={calculatedExpiryDate !== expiryDate ? translate('resources.expiry_date_display.text.click_to_reset_expiry') : undefined}>
            <Chip
              label={TimeGranularityLabel(timeGranularityRounded, translate)}
              onClick={calculatedExpiryDate ? applyExpiryDate : undefined}
              clickable={calculatedExpiryDate !== expiryDate}
              color={isOverridden ? 'warning' : 'primary'}
            />
          </Tooltip>
          <Typography component="span" sx={{ fontWeight: 'bold', lineHeight: 1 }}>=</Typography>
        </Box>
        <Box sx={dateFieldSx}>
          <GrayDateInputWithUpperLabel
            source={props.expiryDateSource}
            label={props.expiryDateLabel}
            size={isFlexibleWidth ? 'medium' : 'small'}
            helperText={props.usage === 'inventoryItems' ? false : undefined}
            inputProps={{
              min: minExpiryDate,
            }}
            sx={dateInputSx}
            validate={required()}
          />
          {props.usage === 'inventoryItems' && (
            <ExpiryDateHelperText text={props.helperText} />
          )}
        </Box>
        {props.children && (
          <Box sx={{
            marginLeft: isFlexibleWidth ? 0 : 1,
            alignSelf: 'flex-start',
            minWidth: 0,
          }}>
            {props.children}
          </Box>
        )}
      </Box>
      {props.usage === 'certifications' && (
        <Box sx={{ maxWidth: 450, marginTop: 1 }}>
          <ExpiryDateHelperText text={props.helperText} />
        </Box>
      )}
    </Stack>
  );

};
